// Quality Dashboard — Feb 2026.
//
// Aggregate visibility into the Customer Complaints database.
//
//   • Public block (always visible):
//       KPIs (total, top customer, top part), customer-wise counts,
//       part-wise counts, status split.
//
//   • Locked block (Masters password gate):
//       Staff attribution — complaint counts by Inspector Name,
//       Process Owner, Quality Supervisor, Operator Supervisor.
//
// Password gate uses the shared Masters password (`Swami24`) verified
// via the existing `/api/settings/masters-password/verify` endpoint.
// Once unlocked, the choice is remembered in sessionStorage so the user
// isn't asked again in the same tab.

import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Users, Package, WarningOctagon, ChartBar, ArrowsClockwise } from "@phosphor-icons/react";
import { API } from "../App";

const STAFF_UNLOCK_KEY = "quality_staff_unlocked";

const STATUS_STYLE = {
  "Open":        { bg: "#FEE2E2", fg: "#B91C1C" },
  "In Progress": { bg: "#FEF3C7", fg: "#B45309" },
  "Closed":      { bg: "#DCFCE7", fg: "#166534" },
};

export default function QualityDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffUnlocked, setStaffUnlocked] = useState(() => {
    try { return sessionStorage.getItem(STAFF_UNLOCK_KEY) === "1"; }
    catch { return false; }
  });

  const load = () => {
    setLoading(true);
    axios.get(`${API}/customer-complaints/dashboard/summary`, { skipPeriod: true })
      .then(r => setData(r.data))
      .catch(() => toast.error("Failed to load dashboard"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => ({
    total:      data?.total || 0,
    customers:  data?.customers || [],
    parts:      data?.parts || [],
    top_cust:   data?.top_customer,
    top_part:   data?.top_part,
    statuses:   data?.status_counts || {},
    staff:      data?.staff || {},
  }), [data]);

  const openCount = totals.statuses["Open"] || 0;

  return (
    <Layout title="Quality Dashboard">
      <div className="p-4 md:p-6 space-y-6 max-w-[1400px] mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1D2951]" style={{ fontFamily: "Chivo" }}>
              Quality Dashboard
            </h1>
            <p className="text-xs md:text-sm text-[#64748B] mt-0.5">
              Complaint trends across customers and parts. Staff attribution is password-protected.
            </p>
          </div>
          <Button variant="outline" onClick={load} className="h-9" data-testid="qd-refresh">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI title="Total Complaints" value={totals.total} icon={WarningOctagon} tint="#1D2951" />
          <KPI title="Open" value={openCount} icon={ChartBar} tint="#B91C1C" />
          <KPI title="Top Customer" value={totals.top_cust?.name || "—"}
               sub={totals.top_cust ? `${totals.top_cust.count} complaints` : ""}
               icon={Users} tint="#7C3AED" />
          <KPI title="Top Part No" value={totals.top_part?.name || "—"}
               sub={totals.top_part ? `${totals.top_part.count} complaints` : ""}
               icon={Package} tint="#0EA5E9" />
        </div>

        {loading && (
          <Card className="p-12 text-center text-[#64748B]">Loading…</Card>
        )}

        {!loading && totals.total === 0 && (
          <Card className="p-12 text-center text-[#94A3B8]" data-testid="qd-empty">
            No complaints on file yet. Create a complaint to populate this dashboard.
          </Card>
        )}

        {!loading && totals.total > 0 && (
          <>
            {/* Customer-wise counts */}
            <Card className="p-5 border border-[#E2E8F0]">
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} className="text-[#7C3AED]" />
                <h2 className="text-sm font-black uppercase tracking-widest text-[#1D2951]">Customer-wise Complaints</h2>
              </div>
              <BarList rows={totals.customers} maxRows={12} testIdPrefix="qd-cust" />
            </Card>

            {/* Part-wise counts */}
            <Card className="p-5 border border-[#E2E8F0]">
              <div className="flex items-center gap-2 mb-3">
                <Package size={16} className="text-[#0EA5E9]" />
                <h2 className="text-sm font-black uppercase tracking-widest text-[#1D2951]">Parts with Highest Complaints</h2>
              </div>
              <BarList rows={totals.parts} maxRows={12} testIdPrefix="qd-part" />
            </Card>

            {/* Status breakdown */}
            <Card className="p-5 border border-[#E2E8F0]">
              <div className="flex items-center gap-2 mb-3">
                <ChartBar size={16} className="text-[#F59E0B]" />
                <h2 className="text-sm font-black uppercase tracking-widest text-[#1D2951]">Status Breakdown</h2>
              </div>
              <div className="flex flex-wrap gap-3">
                {Object.entries(totals.statuses).map(([status, count]) => {
                  const s = STATUS_STYLE[status] || { bg: "#F1F5F9", fg: "#334155" };
                  return (
                    <div key={status} className="px-4 py-3 rounded-lg border" style={{ backgroundColor: s.bg, borderColor: s.fg + "33" }}>
                      <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: s.fg }}>{status}</div>
                      <div className="text-2xl font-black" style={{ color: s.fg }}>{count}</div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* ── Locked: Staff Attribution ── */}
            {staffUnlocked ? (
              <StaffPanel staff={totals.staff} />
            ) : (
              <StaffLockCard onUnlock={() => {
                try { sessionStorage.setItem(STAFF_UNLOCK_KEY, "1"); } catch {/* ignore */}
                setStaffUnlocked(true);
              }} />
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

/* ─────────────────── Sub-components ─────────────────── */

function KPI({ title, value, sub, icon: Icon, tint }) {
  return (
    <Card className="p-4 border border-[#E2E8F0]">
      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest" style={{ color: tint }}>
        <Icon size={14} weight="fill" />
        {title}
      </div>
      <div className="mt-1 text-2xl md:text-3xl font-black text-[#0F172A] truncate" title={String(value)}>{value}</div>
      {sub && <div className="text-[11px] text-[#64748B] font-mono">{sub}</div>}
    </Card>
  );
}

function BarList({ rows, maxRows = 10, testIdPrefix }) {
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-[#94A3B8] italic">No data to display</p>;
  }
  const max = rows[0]?.count || 1;
  const displayed = rows.slice(0, maxRows);
  return (
    <div className="space-y-2">
      {displayed.map((r, i) => {
        const pct = Math.max(4, Math.round((r.count / max) * 100));
        return (
          <div key={r.name} className="flex items-center gap-3" data-testid={`${testIdPrefix}-${i}`}>
            <div className="w-40 md:w-56 shrink-0 text-xs text-[#334155] truncate font-semibold" title={r.name}>{r.name}</div>
            <div className="flex-1 h-6 bg-[#F1F5F9] rounded overflow-hidden relative">
              <div className="h-full rounded" style={{
                width: `${pct}%`,
                background: `linear-gradient(90deg, #1D2951, #D4A843)`,
              }} />
              <span className="absolute inset-0 flex items-center px-2 text-[11px] font-black text-white mix-blend-difference">
                {r.count}
              </span>
            </div>
          </div>
        );
      })}
      {rows.length > maxRows && (
        <p className="text-[10px] text-[#94A3B8] italic mt-1">Showing top {maxRows} of {rows.length}.</p>
      )}
    </div>
  );
}

function StaffPanel({ staff }) {
  const groups = [
    ["Inspectors",           staff.inspector_name],
    ["Process Owners",       staff.process_owner],
    ["Quality Supervisors",  staff.quality_supervisor],
    ["Operator Supervisors", staff.operator_supervisor],
  ];
  const anyData = groups.some(([, rows]) => (rows || []).length > 0);

  return (
    <Card className="p-5 border-2 border-[#D4A843]" data-testid="qd-staff-panel">
      <div className="flex items-center gap-2 mb-4">
        <Lock size={16} weight="fill" className="text-[#D4A843]" />
        <h2 className="text-sm font-black uppercase tracking-widest text-[#1D2951]">Staff Attribution</h2>
        <span className="text-[10px] font-bold text-[#B45309] bg-[#FEF3C7] px-2 py-0.5 rounded">Restricted</span>
      </div>
      {!anyData && (
        <p className="text-xs text-[#94A3B8] italic">No staff data captured on the recorded complaints yet.</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {groups.map(([label, rows]) => (
          <div key={label}>
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#64748B] mb-2">
              {label} — Complaint Rate
            </div>
            <BarList rows={rows || []} maxRows={8} testIdPrefix={`qd-staff-${label.replace(/\s+/g, '-').toLowerCase()}`} />
          </div>
        ))}
      </div>
    </Card>
  );
}

function StaffLockCard({ onUnlock }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) { setError("Enter the password"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/settings/masters-password/verify`,
        { password: password.trim() }, { skipPeriod: true });
      if (r.data?.valid) {
        onUnlock();
        setError("");
      } else {
        setError("Wrong password — try again");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Verification failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Card className="p-8 border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC]" data-testid="qd-staff-lock">
      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="w-16 h-16 rounded-full bg-[#1D2951] flex items-center justify-center shrink-0">
          <Lock size={28} weight="bold" className="text-white" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-lg font-black text-[#1D2951]">Staff Attribution — Restricted</h3>
          <p className="text-xs text-[#64748B] mt-1">
            Complaint rates tied to Inspector, Process Owner, Quality Supervisor and Operator Supervisor are visible only to authorised users.
          </p>
        </div>
        <form onSubmit={submit} className="flex items-end gap-2 shrink-0 w-full md:w-auto">
          <div className="flex-1 md:w-56">
            <Label className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Password</Label>
            <Input type="password" value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter access password"
              className="h-10 mt-1" data-testid="qd-staff-pwd" autoFocus />
          </div>
          <Button type="submit" disabled={submitting}
            className="bg-[#1D2951] hover:bg-[#2A3B73] h-10" data-testid="qd-staff-unlock">
            {submitting ? "…" : "Unlock"}
          </Button>
        </form>
      </div>
      {error && <p className="text-xs text-[#EF4444] mt-2 text-center md:text-right" data-testid="qd-staff-err">{error}</p>}
    </Card>
  );
}
